import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import {
  autoUpdate,
  flip,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { clearHistoryAction } from '../../redux/actions/scheduler';
import { IconWrapper, SvgIcon } from '../Icon';
import { FloatingLayer } from '../FloatingManager';
import { floatingComponentStyles } from '../../styles/shareableStyles';
import KubitTooltip from '../KubitTooltip';

interface IProps {
  children: ReactNode;
}

const Container = styled.div`
  ${floatingComponentStyles};
`;

const ExecutionHistoryPopover = ({ children }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useAppDispatch();
  const isScreenshotModalOpen = useAppSelector(
    (state) => state.scheduler.isScreenshotModalOpen,
  );

  const { floatingStyles, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open && !isScreenshotModalOpen) {
        dispatch(clearHistoryAction());
      }
    },
    middleware: [flip()],
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <KubitTooltip
        content={'Show execution history.'}
        placement="bottom"
        noOpenDelay={true}
        disabled={isOpen}
      >
        <IconWrapper ref={refs.setReference} size={24} {...getReferenceProps()}>
          <SvgIcon name="clock-fill" width={18} height={18} isActive={isOpen} />
        </IconWrapper>
      </KubitTooltip>
      {isOpen && (
        <FloatingLayer>
          <Container
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {children}
          </Container>
        </FloatingLayer>
      )}
    </>
  );
};

export default ExecutionHistoryPopover;
